import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { httpGet, httpPost, httpPut } from '../../helpers/http.js';

// Connections (deployments) of the portal in the LTI Advantage Platform, managed by the portal's
// own admins. A connection is self-contained: it carries its own URLs and custom parameters.
// Suppliers (tools) are global presets whose values are copied into a new connection.

const deploymentsUrl = (env) => `${env.settings.ltiAdvantageApi}/api/portals/${env.portalIdentifier}/deployments`;

// Pseudo tool id in the preset picker for "a supplier that is not in the list"
export const NEW_TOOL_ID = '__new_tool__';

// Display name of a connection: the supplier name. The context label is for internal use.
export const deploymentLabel = (deployment) => {
    if (!deployment) {
        return '';
    }

    return deployment.name || deployment.contextTitle || deployment.contextId;
};

// Label for pickers: marks connections that still miss their URLs and cannot launch yet.
export const deploymentOptionLabel = (deployment, notConfiguredText) => deployment?.isConfigured === false
    ? `${deploymentLabel(deployment)} (${notConfiguredText})`
    : deploymentLabel(deployment);

export const loadDeployments = createAsyncThunk(
    'ltiAdvantageDeployments/fetch',
    async (_, { getState }) => {
        const { env, auth } = getState();

        return httpGet(deploymentsUrl(env), auth.user);
    },
    {
        // GLOBAL is not a portal in the platform
        condition: (_, { getState }) => !getState().env.hasGlobalId,
    },
);

export const loadToolPresets = createAsyncThunk(
    'ltiAdvantageDeployments/fetchPresets',
    async (_, { getState }) => {
        const { env, auth } = getState();

        return httpGet(`${env.settings.ltiAdvantageApi}/api/tools`, auth.user);
    },
);

export const saveDeployment = createAsyncThunk(
    'ltiAdvantageDeployments/save',
    async (args, { getState }) => {
        const { deployment } = args;
        const { env, auth } = getState();
        const body = {
            name: deployment.name,
            jwksUrl: deployment.jwksUrl,
            loginUrl: deployment.loginUrl,
            redirectUrl: deployment.redirectUrl,
            deepLinkingUrl: deployment.deepLinkingUrl,
            customParams: deployment.customParams,
        };

        if (deployment.contextId) {
            return httpPut(`${deploymentsUrl(env)}/${deployment.contextId}`, auth.user, body);
        }

        // With a preset the platform copies its values first; the fields sent here replace them.
        return httpPost(deploymentsUrl(env), auth.user, {
            toolId: deployment.toolId && deployment.toolId !== NEW_TOOL_ID ? deployment.toolId : null,
            ...body,
        });
    },
);

const registrationsUrl = (env) => `${env.settings.ltiAdvantageApi}/api/portals/${env.portalIdentifier}/registrations`;

// Dynamic registration (LTI DR 1.0): the platform mints a one-time token and the supplier's page
// registers the connection itself. The editor opens the initiation url in a popup and follows the
// status until the supplier is done.
export const startRegistration = createAsyncThunk(
    'ltiAdvantageDeployments/startRegistration',
    async (args, { getState }) => {
        const { toolRegistrationUrl } = args;
        const { env, auth } = getState();

        return httpPost(registrationsUrl(env), auth.user, { toolRegistrationUrl });
    },
);

export const pollRegistration = createAsyncThunk(
    'ltiAdvantageDeployments/pollRegistration',
    async (args, { getState }) => {
        const { initiationId } = args;
        const { env, auth } = getState();

        return httpGet(`${registrationsUrl(env)}/${initiationId}`, auth.user);
    },
);

export const loadPlatformConfig = createAsyncThunk(
    'ltiAdvantageDeployments/fetchPlatformConfig',
    async (args, { getState }) => {
        const { contextId } = args;
        const { env, auth } = getState();

        const config = await httpGet(`${deploymentsUrl(env)}/${contextId}/platform-config`, auth.user);

        return { contextId, ...config };
    },
);

const deploymentsSlice = createSlice({
    name: 'ltiAdvantageDeployments',
    initialState: {
        status: 'idle',
        list: [],
        presets: [],
        presetsStatus: 'idle',
        platformConfig: null,
        // the dynamic registration in progress: { initiationId, initiationUrl, expiresAt, status, deployment, error }
        registration: null,
        error: null,
    },
    reducers: {
        clearRegistration: (state) => {
            state.registration = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(startRegistration.pending, (state) => {
                state.registration = { status: 'starting' };
            })
            .addCase(startRegistration.fulfilled, (state, action) => {
                state.registration = { ...action.payload, status: 'pending' };
            })
            .addCase(startRegistration.rejected, (state, action) => {
                state.registration = { status: 'failed', error: action.error?.message };
            })
            .addCase(pollRegistration.fulfilled, (state, action) => {
                const { payload } = action;
                if (state.registration?.initiationId !== payload.initiationId) {
                    return;
                }

                state.registration = { ...state.registration, ...payload };

                // a completed registration yields a (new or updated) connection
                if (payload.status === 'completed' && payload.deployment) {
                    const index = state.list.findIndex((d) => d.contextId === payload.deployment.contextId);
                    if (index >= 0) {
                        state.list[index] = payload.deployment;
                    } else {
                        state.list.push(payload.deployment);
                    }
                }
            })
            .addCase(loadDeployments.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(loadDeployments.fulfilled, (state, action) => {
                state.status = 'success';
                state.list = action.payload;
            })
            .addCase(loadDeployments.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error;
            })
            .addCase(loadToolPresets.pending, (state) => {
                state.presetsStatus = 'loading';
            })
            .addCase(loadToolPresets.fulfilled, (state, action) => {
                state.presetsStatus = 'success';
                state.presets = action.payload;
            })
            .addCase(loadToolPresets.rejected, (state, action) => {
                state.presetsStatus = 'failed';
                state.error = action.error;
            })
            .addCase(saveDeployment.pending, (state) => {
                state.status = 'saving';
            })
            .addCase(saveDeployment.fulfilled, (state, action) => {
                const { payload } = action;
                const index = state.list.findIndex((d) => d.contextId === payload.contextId);

                state.status = 'success';
                if (index >= 0) {
                    state.list[index] = payload;
                } else {
                    state.list.push(payload);
                }
            })
            .addCase(saveDeployment.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error;
            })
            .addCase(loadPlatformConfig.pending, (state) => {
                state.platformConfig = null;
            })
            .addCase(loadPlatformConfig.fulfilled, (state, action) => {
                state.platformConfig = action.payload;
            })
            .addCase(loadPlatformConfig.rejected, (state, action) => {
                state.error = action.error;
            });
    },
});

export const { clearRegistration } = deploymentsSlice.actions;

export default deploymentsSlice.reducer;
